<template>
  <div class="min-h-screen bg-gradient-to-b from-gray-800 to-gray-900">
    <!-- Header -->
    <header class="w-full px-[12.5%] py-6 backdrop-blur-sm bg-gray-800/50 border-b border-gray-700">
      <div class="max-w-6xl mx-auto flex justify-between items-center">
        <h1 class="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300">My Blog</h1>
        <div class="relative w-64">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search articles..."
            class="w-full px-4 py-2 rounded-lg bg-gray-700/50 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-400/30 text-white placeholder-gray-400 transition-all"
          />
          <svg
            class="absolute right-3 top-2.5 h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="w-full px-[12.5%] py-12">
      <div class="max-w-6xl mx-auto">
        <!-- Category Filters -->
        <div class="mb-12 flex flex-wrap gap-3 justify-center animate-fade-in">
          <button
            v-for="category in categories"
            :key="category"
            @click="setActiveCategory(category)"
            :class="[
              'px-4 py-2 rounded-full text-sm font-medium transition-all duration-300',
              activeCategory === category
                ? 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white shadow-lg'
                : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 border border-gray-600'
            ]"
          >
            {{ category }}
          </button>
        </div>

        <!-- Articles Grid -->
        <div class="text-center mb-16 animate-fade-in">
          <h2 class="text-4xl text-white font-bold mb-4">
            Recent <span class="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300">Articles</span>
          </h2>
          <p class="text-xl text-gray-400 max-w-3xl mx-auto">
            Thoughts on {{ activeCategory || 'technology, creativity, and everything in between' }}
          </p>
        </div>

        <div ref="postsContainer" class="grid md:grid-cols-1 lg:grid-cols-1 gap-8 max-sm:gap-4">
          <div 
            v-for="post in currentPagePosts" 
            :key="post.id" 
            @mousedown="viewPost(post, $event)"
            class="group relative cursor-pointer overflow-hidden rounded-2xl bg-gray-800/50 backdrop-blur-sm border border-gray-700 hover:border-blue-400/30 transition-all duration-500 sm:hover:-translate-y-2"
          >
            <div class="p-6">
              <div class="flex items-center gap-3 w-full justify-between sm:mb-4">
                <div class="flex items-center max-sm:items-start gap-3">
                  <div class="w-10 h-10 max-sm:hidden rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 flex items-center justify-center text-white">
                    {{ getCategoryEmoji(post.category) }}
                  </div>
                  <h3 class="text-2xl max-sm:text-xl font-bold sm:mb-3 text-white cursor-pointer max-sm:order-2 group-hover:text-blue-300 transition-colors">
                    {{ post.title }}
                  </h3>
                  <span class="text-sm text-gray-400 max-sm:order-1">{{ formatRelativeDate(post.date) }}</span>
                </div>
                <a 
                  href="#" 
                  class="inline-flex max-sm:hidden items-center float-right text-blue-300 hover:text-cyan-300 group transition-colors"
                >
                  <span class="mr-2 font-medium">Read article</span>
                  <span class="group-hover:translate-x-1 transition-transform">→</span>
                </a>
              </div>
              <p class="text-gray-400 max-sm:hidden" v-html="post.excerpt"></p>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="mt-16 flex justify-center animate-fade-in">
          <nav class="flex items-center gap-2">
            <button
              v-for="page in totalPages"
              :key="page"
              @click="goToPage(page)"
              :class="[
                'px-5 py-2 rounded-full text-sm font-medium transition-all',
                currentPage === page
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white shadow-md'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 border border-gray-600'
              ]"
            >
              {{ page }}
            </button>
          </nav>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="w-full px-[12.5%] py-12 border-t border-gray-800 bg-gray-900/50">
      <div class="max-w-6xl mx-auto">
        <div class="flex flex-col md:flex-row justify-between items-center">
          <div class="mb-6 md:mb-0">
            <h2 class="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300 mb-2">
              My Blog
            </h2>
            <p class="text-gray-400">Thoughts on technology, creativity, and everything in between</p>
          </div>
          <div class="flex gap-4">
            <a href="#" class="text-gray-400 hover:text-blue-300 transition-colors">
              <span class="sr-only">Twitter</span>
              <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
              </svg>
            </a>
            <a href="#" class="text-gray-400 hover:text-blue-300 transition-colors">
              <span class="sr-only">GitHub</span>
              <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path fill-rule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clip-rule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
        <div class="mt-8 pt-8 border-t border-gray-800 text-center text-gray-500 text-sm">
          © 2023 My Blog. All rights reserved.
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeMount, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import blogPosts from '@/assets/Posts/blogPosts.js'

const router = useRouter()
const route = useRoute()
const postsContainer = ref(null)

// State
const searchQuery = ref('')
const activeCategory = ref(null)
const currentPage = ref(1)
const postsPerPage = ref(10) // Adjust as needed

// Initialize from route
onBeforeMount(() => {
  if (route.params.page) {
    currentPage.value = Number(route.params.page)
  }
})

// Categories
const categories = [
  'Dev',
  'Islam',
  'Farming',
  'Politics',
  'Art',
  'Maths',
  'Physics',
  'Medicine',
  'Bodybuilding'
]


// Computed properties
const filteredPosts = computed(() => {
  let filtered = blogPosts

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(post =>
      post.title.toLowerCase().includes(query) ||
      post.excerpt.toLowerCase().includes(query)
    )
  }

  if (activeCategory.value) {
    filtered = filtered.filter(post => post.category === activeCategory.value)
  }

  // Sort by ID string in ascending order
  return filtered.sort((a, b) => {
    return b.id.localeCompare(a.id);
  });
})


const totalPages = computed(() => {
  return Math.ceil(filteredPosts.value.length / postsPerPage.value)
})

const currentPagePosts = computed(() => {
  const start = (currentPage.value - 1) * postsPerPage.value
  const end = start + postsPerPage.value
  return filteredPosts.value.slice(start, end)
})

// Methods
function setActiveCategory(category) {
  activeCategory.value = activeCategory.value === category ? null : category
  currentPage.value = 1
}

function formatRelativeDate(dateString) {
  const date = new Date(dateString)
  const now = new Date()
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))
  
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays/7)} weeks ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function getCategoryEmoji(category) {
  const emojis = {
    'Dev': '💻',
    'Islam': '🕌',
    'Art': '🎨',
    'Relationships': '💞',
    'Maths': '🧮',
    'Physics': '⚛️',
    'Medicine': '🩺',
    'Bodybuilding': '💪'
  }
  return emojis[category] || '✍️'
}

function viewPost(post, event) {
  localStorage.setItem('blogPage', currentPage.value.toString());

  // Generate the target URL using the router for accuracy
  const targetUrl = router.resolve(`/post/${post.id.toString().padStart(4, '0')}`).href;

  // Check for middle mouse button OR Ctrl key + left mouse button
  if (event.button === 1 || (event.button === 0 && event.ctrlKey)) {
    event.preventDefault(); // Prevent default browser action (like text selection drag on middle click)
    window.open(targetUrl, '_blank'); // Open in a new tab
    // Optional: Release focus from the clicked element after opening the new tab
    if (event.currentTarget && typeof event.currentTarget.blur === 'function') {
        event.currentTarget.blur();
    }
  }
  // Check for left mouse button WITHOUT Ctrl key
  else if (event.button === 0 && !event.ctrlKey) {
    event.preventDefault(); // Prevent default action (important if it was an <a> tag)
    router.push(`/post/${post.id.toString().padStart(4, '0')}`); // Navigate in the same tab
  }
}

function goToPage(page) {
  currentPage.value = page
  router.push(`/blog/${page}`)
  
  // Scroll to top of posts container
  nextTick(() => {
    if (postsContainer.value) {
      postsContainer.value.scrollIntoView({ behavior: 'smooth' })
    }
  })
}

// Reset when filters change
watch([searchQuery, activeCategory], () => {
  currentPage.value = 1
})
</script>

<style>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in {
  animation: fadeIn 0.6s ease-out forwards;
}

.backdrop-blur-sm {
  backdrop-filter: blur(8px);
}

.bg-gray-800 {
  background-color: rgba(31, 41, 55, 0.5);
}
</style>